import React, { useState, useEffect } from "react";
import { Table, Spin, Card, Tag, Progress, Modal, Button, Input, Typography, App } from "antd";
import noDataImage from "../assets/icon/ARShades_Logo_Sorry.svg";
import dayjs from "dayjs";
import { OutlineButton } from "../components/Components/Buttons/Buttons";
import ConsumptionDetailsModal from "../components/DataConsumption/ConsumptionDetailsModal";
import HeaderComponent from "../components/Components/Headers/HeaderComponent";
import CustomTooltip from "../components/Components/Modals/CustomTooltip";
import { InfoCircleOutlined } from '@ant-design/icons';
import { primaryShades, greyScaleTokens } from "../theme/theme";
import { getUserProfileForEmail } from "../services/emailService";
import { sendSubscriptionRequestEmail } from "../services/emailService";
import AddIcon from '@mui/icons-material/Add';
import ToggleButtons from "../components/Components/Buttons/ToggleButtons";
import { useSubscriptions } from "../services/hooks/useSubscriptions";

const { Title, Text } = Typography;

const DataConsumption = () => {
    // Hook per accedere al message API
    const { message } = App.useApp();

    // Component shows subscriptions based on selected status (Active/Expired)
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [subscriptionType, setSubscriptionType] = useState('active');
    const [error, setError] = useState(null);

    // Load More functionality
    const [visibleItems, setVisibleItems] = useState(30); // Mostra i primi 30 elementi
    const ITEMS_PER_LOAD = 30; // Carica 30 elementi alla volta

    // Use the custom hook for subscriptions with cache
    const {
        subscriptions,
        loading,
        userRole,
        subscriptionsCache,
        fetchSubscriptions,
        getSubscriptionsFromCache
    } = useSubscriptions();

    // Stati per la modale di upgrade (copiati da AddToCatalogStepper)
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, subscription: null });
    const [upgradeMessage, setUpgradeMessage] = useState('');
    const [sendingUpgradeRequest, setSendingUpgradeRequest] = useState(false);

    // Funzione per determinare se l'utente può aggiungere subscription
    const canAddSubscription = (role) => {
        // Se il ruolo non è ancora caricato, nascondi il bottone
        if (!role) return false;
        // Il Validator non può aggiungere subscription
        return role !== 'Validator';
    };

    // Function to handle subscription type change with cache optimization
    const handleSubscriptionTypeChange = (newType) => {
        setSubscriptionType(newType);
        setVisibleItems(30); // Reset visible items quando cambia il tipo

        // Check if we have cached data for the new type
        if (subscriptionsCache[newType]) {
            // Use cached data - no API call needed!
            getSubscriptionsFromCache(newType);
        } else {
            // Fetch new data only if not in cache
            fetchSubscriptions(newType);
        }
    };

    // Function to handle Load More
    const handleLoadMore = () => {
        setVisibleItems(prev => prev + ITEMS_PER_LOAD);
    };

    // Get visible subscriptions for current page
    const visibleSubscriptions = subscriptions.slice(0, visibleItems);
    const hasMoreItems = subscriptions.length > visibleItems;

    // Load initial data on component mount
    useEffect(() => {
        fetchSubscriptions(subscriptionType);
    }, []); // Solo al mount, poi useremo la cache

    // Handle modal open/close
    const handleOpenModal = (subscription) => {
        setSelectedSubscription(subscription);
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setSelectedSubscription(null);
    };



    // Function to format dates
    const formatDate = (date) => {
        if (!date) return 'N/A';

        if (date.seconds) {
            return dayjs.unix(date.seconds).format('DD/MM/YYYY');
        }

        return dayjs(date).format('DD/MM/YYYY');
    };



    // Function to calculate days left
    const calculateDaysLeft = (subscription) => {
        if (!subscription.endDate || !subscription.startDate) return { daysLeft: 'ND', percentage: 0, color: '#d9d9d9' };

        const today = dayjs();
        let startDate, endDate;

        // Handle different date formats for startDate
        if (typeof subscription.startDate === 'string') {
            startDate = dayjs(subscription.startDate);
        } else if (subscription.startDate.seconds) {
            startDate = dayjs.unix(subscription.startDate.seconds);
        } else {
            startDate = dayjs(subscription.startDate);
        }

        // Handle different date formats for endDate
        if (typeof subscription.endDate === 'string') {
            endDate = dayjs(subscription.endDate);
        } else if (subscription.endDate.seconds) {
            endDate = dayjs.unix(subscription.endDate.seconds);
        } else {
            endDate = dayjs(subscription.endDate);
        }

        const totalDays = endDate.diff(startDate, 'day');
        const daysLeft = endDate.diff(today, 'day');
        const daysPassed = today.diff(startDate, 'day');

        // Calculate percentage based on days passed vs total days
        const percentage = totalDays > 0 ? Math.min((daysPassed / totalDays) * 100, 100) : 0;

        // Determine color based on days left percentage
        const daysLeftPercentage = totalDays > 0 ? (daysLeft / totalDays) * 100 : 0;
        let color;

        if (daysLeft < 0) {
            // Subscription expired
            color = '#d9d9d9'; // Grey (Expired)
        } else if (daysLeftPercentage > 50) {
            color = '#52c41a'; // Green (Primary)
        } else if (daysLeftPercentage > 25) {
            color = '#faad14'; // Orange (Warning)
        } else {
            color = '#ff4d4f'; // Red (Danger)
        }

        return {
            daysLeft: daysLeft < 0 ? Math.abs(daysLeft) : daysLeft,
            percentage: Math.max(percentage, 0),
            color,
            isExpired: daysLeft < 0
        };
    };

    // Table columns definition
    const columns = [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Type</span>
                    <CustomTooltip title="Subscription type. MAIN indicates primary subscriptions, ADD-ON indicates additional services linked to main subscriptions.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'level',
            key: 'level',
            width: 90,
            render: (level, record) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: level === 2 ? '1.25rem' : '0rem'
                }}>
                    {level === 2 && <span style={{ marginRight: '0.5rem' }}>└─</span>}
                    <Tag color={level === 1 ? (record.isMainSubscription ? 'green' : 'blue') : 'blue'}>
                        {level === 1 ?
                            (record.isMainSubscription ? 'MAIN' : 'ADD-ON') :
                            'ADD-ON'
                        }
                    </Tag>
                </div>
            ),
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Plan Name</span>
                    <CustomTooltip title="Name of the subscription plan. Main subscriptions are shown in bold, add-ons in regular text.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'name',
            key: 'name',
            width: 250,
            render: (name, record) => (
                <span style={{
                    fontWeight: record.level === 1 ? 'bold' : 'normal',
                    color: record.level === 1 ? '#333' : '#666'
                }}>
                    {name || 'N/A'}
                </span>
            ),
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Plan Type</span>
                    <CustomTooltip title="Type of subscription plan (e.g., ARShades Branded License, ARShades Smart License, etc.).">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'subscriptionType',
            key: 'subscriptionType',
            width: 200,
            render: (type) => type || 'N/A',
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Consumption</span>
                    <CustomTooltip title="Data consumption in GB. Shows used/limit ratio. Red text and 'LIMIT EXCEEDED' badge indicate when consumption exceeds the subscription limit.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'consumption',
            width: 120,
            render: (_, record) => {
                const gbUsed = record.gbUsed || 0;
                const consumptionLimit = record.list_catalogues?.consumptionLimit || 0;

                // Controlla se ha superato il limite GB
                const gbExceeded = consumptionLimit > 0 && gbUsed > consumptionLimit;

                return (
                    <div style={{
                        fontSize: '0.875rem',
                        color: gbExceeded ? '#ff4d4f' : '#333',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontWeight: 'bold' }}>
                            {gbUsed.toFixed(1)}/{consumptionLimit === 0 ? '∞' : consumptionLimit} GB
                        </div>
                        {gbExceeded && (
                            <div style={{
                                marginTop: '0.25rem',
                                padding: '0.125rem 0.25rem',
                                backgroundColor: '#fff2f0',
                                border: '1px solid #ffccc7',
                                borderRadius: '0.25rem',
                                fontSize: '0.625rem',
                                fontWeight: 'bold',
                                color: '#ff4d4f'
                            }}>
                                LIMIT EXCEEDED
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Start Date</span>
                    <CustomTooltip title="Subscription start date. When the subscription plan became active.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'startDate',
            key: 'startDate',
            width: 120,
            render: (date) => formatDate(date),
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>End Date</span>
                    <CustomTooltip title="Subscription end date. When the subscription plan expires.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'endDate',
            key: 'endDate',
            width: 120,
            render: (date) => formatDate(date),
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>{subscriptionType === 'active' ? 'Days Left' : 'Days Since Expiry'}</span>
                    <CustomTooltip title={subscriptionType === 'active'
                        ? "Days remaining until subscription expiration. Progress bar shows time elapsed from start to end date. Green: >50% time left, Orange: 25-50% time left, Red: <25% time left."
                        : "Days elapsed since subscription expiration. Progress bar shows complete duration from start to end date."
                    }>
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'daysLeft',
            width: 120,
            render: (_, record) => {
                const { daysLeft, percentage, color, isExpired } = calculateDaysLeft(record);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ flex: 1, minWidth: '60px' }}>
                            <Progress
                                percent={percentage}
                                showInfo={false}
                                strokeColor={color}
                                trailColor="#f0f0f0"
                                size="small"
                            />
                        </div>
                        <span style={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: isExpired ? '#ff4d4f' : '#333',
                            minWidth: '30px',
                            textAlign: 'center'
                        }}>
                            {isExpired && subscriptionType === 'expired' ? `+${daysLeft}` : daysLeft}
                        </span>
                    </div>
                );
            },
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Actions</span>
                    <CustomTooltip title="Available actions for the subscription. Click 'Details' to view consumption charts and detailed information.">
                        <InfoCircleOutlined style={{ fontSize: '0.875rem' }} />
                    </CustomTooltip>
                </div>
            ),
            key: 'actions',
            width: 100,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <OutlineButton
                    onClick={() => handleOpenModal(record)}
                >
                    Details
                </OutlineButton>
            ),
        },
    ];

    return (
        <div style={{ position: "relative", minHeight: "80svh" }}>
            <HeaderComponent
                title="Subscriptions plans"
                buttonText="Add Subscription"
                buttonIcon={<AddIcon />}
                onButtonClick={() => setUpgradeModal({ visible: true, subscription: null })}
                useSpaceBetween={true}
                isVisible={canAddSubscription(userRole)}
                rightContent={
                    <>
                        {/* Container per centrare il toggle */}
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <ToggleButtons
                                value={subscriptionType}
                                onChange={handleSubscriptionTypeChange}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'expired', label: 'Expired' }
                                ]}
                                type="button"
                                style={{ marginTop: '0.5rem' }}
                            />
                        </div>
                    </>
                }
            />

            {loading ? (
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <Spin size="large" />
                </div>
            ) : error ? (
                <Card style={{ textAlign: "center", padding: "2.5rem" }}>
                    <img
                        src={noDataImage}
                        alt="Error"
                        style={{ width: "12.5rem", marginBottom: "1.25rem" }}
                    />
                    <p style={{ fontSize: "1rem", color: "#ff4d4f" }}>
                        {error}
                    </p>
                </Card>
            ) : subscriptions.length === 0 ? (
                <Card style={{ textAlign: "center", padding: "2.5rem" }}>
                    <img
                        src={noDataImage}
                        alt="No data available"
                        style={{ width: "12.5rem", marginBottom: "1.25rem" }}
                    />
                    <p style={{ fontSize: "1rem", color: "#555" }}>
                        {subscriptionType === 'active'
                            ? "No active subscriptions found for this user."
                            : "No subscription history found for this user."
                        }
                    </p>
                </Card>
            ) : (
                <div style={{ marginTop: '1.5rem' }}>
                    <Table
                        dataSource={visibleSubscriptions}
                        columns={subscriptionType === 'expired'
                            ? columns.filter(col => col.key !== 'daysLeft')
                            : columns
                        }
                        rowKey="key"
                        loading={loading}
                        pagination={false}
                        size="middle"
                        bordered
                        scroll={{ x: 1070 }}
                    />

                    {/* Load More Button */}
                    {hasMoreItems && (
                        <div style={{
                            textAlign: 'center',
                            marginTop: '1.5rem',
                            padding: '1rem'
                        }}>
                            <OutlineButton
                                onClick={handleLoadMore}
                                style={{
                                    minWidth: '8rem',
                                    height: '2.5rem'
                                }}
                            >
                                Load More ({subscriptions.length - visibleItems} remaining)
                            </OutlineButton>
                        </div>
                    )}
                </div>
            )}

            {/* Consumption Details Modal */}
            <ConsumptionDetailsModal
                visible={modalVisible}
                onClose={handleCloseModal}
                subscription={selectedSubscription}
            />

            {/* Subscription Request Modal - Modificato per nuove subscription */}
            <Modal
                title={
                    <div style={{ textAlign: 'center', paddingBottom: '0.625rem' }}>
                        <Title level={4} style={{ margin: 0, color: primaryShades.primary300 }}>
                            Request New Subscription
                        </Title>
                    </div>
                }
                open={upgradeModal.visible}
                onCancel={() => {
                    setUpgradeModal({ visible: false, subscription: null });
                    setUpgradeMessage('');
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setUpgradeModal({ visible: false, subscription: null });
                        setUpgradeMessage('');
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        loading={sendingUpgradeRequest}
                        onClick={async () => {
                            if (!upgradeMessage.trim()) {
                                message.error('Please enter a message for the subscription request.');
                                return;
                            }

                            setSendingUpgradeRequest(true);

                            try {
                                // Ottieni le informazioni del profilo utente
                                const userProfile = await getUserProfileForEmail();

                                // Invia la richiesta per nuova subscription
                                await sendSubscriptionRequestEmail(userProfile, upgradeMessage);

                                message.success('Subscription request sent successfully! You will receive a confirmation email shortly.');
                                setUpgradeModal({ visible: false, subscription: null });
                                setUpgradeMessage('');
                            } catch (error) {
                                console.error('Error sending subscription request:', error);
                                message.error('Failed to send subscription request. Please try again.');
                            } finally {
                                setSendingUpgradeRequest(false);
                            }
                        }}
                    >
                        {sendingUpgradeRequest ? 'Sending Request...' : 'Send Request'}
                    </Button>
                ]}
                centered
                width="37.5rem"
                maskClosable={!sendingUpgradeRequest}
                closable={!sendingUpgradeRequest}
            >
                <div style={{ padding: '1.25rem 0' }}>
                    {/* Messaggio informativo */}
                    <div style={{
                        backgroundColor: primaryShades.primary100,
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1.25rem',
                        border: `1px solid ${primaryShades.primary200}`
                    }}>
                        <Text style={{ color: primaryShades.primary600 }}>
                            <strong>📋 New Subscription Request</strong>
                        </Text>
                        <br />
                        <Text style={{ color: greyScaleTokens.grey700 }}>
                            A dedicated section for subscription management will be available soon.
                            For now, you can request a quote for a new subscription by sending an email with your specifications to our team.
                        </Text>
                    </div>

                    {/* Campo per il messaggio */}
                    <div>
                        <Title level={5} style={{ marginBottom: '0.5rem' }}>
                            Describe your subscription requirements:
                        </Title>
                        <Input.TextArea
                            rows={4}
                            value={upgradeMessage}
                            onChange={(e) => setUpgradeMessage(e.target.value)}
                            placeholder={`Please describe:
• What type of subscription you need
• How many SKUs you require
• Your business requirements
• Timeline for activation
• Any other relevant information...`
                            }
                            maxLength={1000}
                            showCount
                            style={{ marginBottom: '0.75rem' }}
                        />
                        <Text style={{ fontSize: '0.75rem', color: greyScaleTokens.grey600 }}>
                            This request will be sent to our support team via email
                        </Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DataConsumption;